import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { createCryptoDepositController } from './crypto-deposit-controller.js';
export function useCryptoDepositCapability(client, surface) {
  const $ = _c(10);
  let t0;
  if ($[0] !== client.cacheKey || $[1] !== surface) {
    t0 = ["onramp", client.cacheKey, "crypto-deposit-capability", surface];
    $[0] = client.cacheKey;
    $[1] = surface;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== client.cryptoDeposit || $[4] !== surface) {
    t1 = t2 => {
      const {
        signal
      } = t2;
      return client.cryptoDeposit?.capability(surface, signal) ?? Promise.resolve(false);
    };
    $[3] = client.cryptoDeposit;
    $[4] = surface;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      persist: false
    };
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t0 || $[8] !== t1) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      retry: false,
      staleTime: 60000,
      gcTime: 0,
      meta: t2
    };
    $[7] = t0;
    $[8] = t1;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return useQuery(t3);
}
export function useCryptoDeposit(client, options) {
  const $ = _c(28);
  const {
    surface,
    locale,
    active,
    operationId,
    createId,
    onComplete
  } = options;
  const t0 = operationId ?? null;
  let t1;
  if ($[0] !== client || $[1] !== createId || $[2] !== locale || $[3] !== surface || $[4] !== t0) {
    t1 = createCryptoDepositController(client, surface, locale, createId, t0);
    $[0] = client;
    $[1] = createId;
    $[2] = locale;
    $[3] = surface;
    $[4] = t0;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const actions = t1;
  const state = useSyncExternalStore(actions.subscribe, actions.getSnapshot, actions.getSnapshot);
  let t2;
  let t3;
  if ($[6] !== actions || $[7] !== client) {
    t2 = () => {
      const unregister = client.onDispose(actions.dispose);
      return () => {
        unregister();
        actions.dispose();
      };
    };
    t3 = [client, actions];
    $[6] = actions;
    $[7] = client;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[10] !== actions || $[11] !== onComplete) {
    t4 = () => {
      if (!onComplete) {
        return;
      }
      return actions.onComplete(onComplete);
    };
    t5 = [actions, onComplete];
    $[10] = actions;
    $[11] = onComplete;
    $[12] = t4;
    $[13] = t5;
  } else {
    t4 = $[12];
    t5 = $[13];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[14] !== actions || $[15] !== active || $[16] !== operationId || $[17] !== state.operation) {
    t6 = () => {
      if (!active || state.operation) {
        return;
      }
      if (operationId) {
        actions.resume(operationId);
      } else {
        actions.start();
      }
    };
    t7 = [actions, active, operationId, state.operation];
    $[14] = actions;
    $[15] = active;
    $[16] = operationId;
    $[17] = state.operation;
    $[18] = t6;
    $[19] = t7;
  } else {
    t6 = $[18];
    t7 = $[19];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[20] !== actions || $[21] !== active || $[22] !== state.operation) {
    t8 = () => {
      const operation = state.operation;
      if (!active || !operation?.instructions || ["complete", "route_failed", "refund"].includes(operation.state)) {
        return;
      }
      const timer = setInterval(() => void actions.refresh(), 7000);
      return () => clearInterval(timer);
    };
    t9 = [actions, active, state.operation];
    $[20] = actions;
    $[21] = active;
    $[22] = state.operation;
    $[23] = t8;
    $[24] = t9;
  } else {
    t8 = $[23];
    t9 = $[24];
  }
  useEffect(t8, t9);
  let t10;
  if ($[25] !== actions || $[26] !== state) {
    t10 = {
      state,
      actions
    };
    $[25] = actions;
    $[26] = state;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
}